import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Form } from 'react-bootstrap'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import {
  selectCrossClientLandingCurrentExecution,
  selectCrossClientInitLoading,
  setCrossClientLandingExecution,
  setCrossClientEvaluateLoading,
  setCrossClientExecutions,
  setCrossClientInitLoading,
  setCrossClientRecentExecutionsLoading,
  resetCrossClientLanding,
  selectCrossClientEvaluateLoading,
} from '../../../store/crossClient/crossClientLanding'
import { ReactComponent as ZocksLogo } from '../../../assets/ZocksLogo.svg'
import { useEvaluateCrossClientQueryMutation, useInitializeCrossClientQueryMutation } from '../../../store/crossClient/crossClientExecution'
import { selectAccountId, selectSafeAuthenticatedUser } from '../../../store/auth/authSlice'
import { CrossClientQueryPhases } from '../../../models/CrossClient'
import { Button } from '../../../components/Button/Button'
import { useLazyGetCrossClientQueryExecutionsQuery } from '../../../store/crossClient/crossClient'
import { useNavigate } from 'react-router-dom'
import classNames from 'classnames'
import { CrossClientClauses } from '../Clauses/CrossClientClauses'
import { CrossClientRunningExecutions } from './RunningExecutions/CrossClientRunningExecutions'
import { CrossClientHistory } from './History/CrossClientHistory'
import { CrossClientLandingDefaults } from './Defaults/CrossClientLandingDefaults'
import './CrossClientLanding.scss'

const MAX_USER_QUESTION_LENGTH = 500 // todo

export const CrossClientLanding = () => {
  const navigate = useNavigate()

  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)
  const { id: currentUserId } = useAppSelector(selectSafeAuthenticatedUser)
  const initLoading = useAppSelector(selectCrossClientInitLoading)
  const currentExecution = useAppSelector(selectCrossClientLandingCurrentExecution)
  const evaluateLoading = useAppSelector(selectCrossClientEvaluateLoading)

  const [userQuestion, setUserQuestion] = useState<string>('')

  const [triggerInitializeCrossClientQuery] = useInitializeCrossClientQueryMutation()
  const [triggerEvaluateCrossClientQuery] = useEvaluateCrossClientQueryMutation()
  const [triggerGetCrossClientExecutions] = useLazyGetCrossClientQueryExecutionsQuery()

  const atomicClauses = currentExecution?.decomposition?.atomicClauses ?? []
  const numberOfClauses = atomicClauses.length

  const initializeCrossClientExecution = useCallback(
    async (userQuestion: string) => {
      try {
        dispatch(setCrossClientLandingExecution(null))
        dispatch(setCrossClientInitLoading(true))
        const execution = await triggerInitializeCrossClientQuery({ accountId, payload: { ownerId: currentUserId, userQuestion } }).unwrap()
        dispatch(setCrossClientLandingExecution(execution))
      } catch (e) {
        console.error(e)
      } finally {
        dispatch(setCrossClientInitLoading(false))
      }
    },
    [dispatch, accountId, currentUserId, triggerInitializeCrossClientQuery],
  )

  const evaluateCrossClientExecution = useCallback(
    async (executionId: string) => {
      try {
        dispatch(setCrossClientEvaluateLoading(true))
        await triggerEvaluateCrossClientQuery({ accountId, executionId }).unwrap()
        navigate(`/cross-client/executions/${executionId}`, { replace: true })
      } catch (e) {
        console.error(e)
        dispatch(setCrossClientEvaluateLoading(false))
      }
    },
    [dispatch, navigate, accountId, triggerEvaluateCrossClientQuery],
  )

  // todo
  const initError = useMemo(() => {
    if (!currentExecution) {
      return null
    }
    const { phase } = currentExecution
    if (
      phase === CrossClientQueryPhases.NO_CLIENTS_WERE_FOUND ||
      phase === CrossClientQueryPhases.NO_CLIENTS_AFTER_FILTER_IDS ||
      phase === CrossClientQueryPhases.NO_CLIENTS_AFTER_FILTER_ROLE
    ) {
      return 'No clients found'
    }
    if (currentExecution.phase === CrossClientQueryPhases.QUESTION_ERROR) {
      return 'Unable to decompose question'
    }
    return null
  }, [currentExecution])

  useEffect(() => {
    const getRecentExecutions = async () => {
      try {
        dispatch(setCrossClientRecentExecutionsLoading(false))
        const executions = await triggerGetCrossClientExecutions({ accountId, pageRequest: { page: 0, size: 5 } }).unwrap()
        if (!executions.data.length) {
          return
        }
        dispatch(setCrossClientExecutions(executions.data))
      } catch (e) {
        console.error(e)
      } finally {
        dispatch(setCrossClientRecentExecutionsLoading(false))
      }
    }

    getRecentExecutions()
  }, [dispatch, accountId, triggerGetCrossClientExecutions])

  useEffect(() => {
    dispatch(resetCrossClientLanding())
  }, [dispatch])

  const handleEvaluate = async () => {
    if (!currentExecution) {
      return
    }
    await evaluateCrossClientExecution(currentExecution.executionId)
  }

  const handleRestart = () => {
    dispatch(setCrossClientLandingExecution(null))
    setUserQuestion('')
  }

  return (
    <div className='d-flex flex-column align-items-center'>
      <div className='d-flex flex-column gap-3 align-items-center'>
        <div className='paragraph-small zocks-mt-4 py-1 zocks-px-0.75 text-uppercase zocks-border-1.0 zocks-bg-color-gray-100 border border-1 zocks-border-color-gray-200 medium zocks-text-gray-700'>
          Client Intelligence
        </div>
        <div className='d-flex flex-column gap-2'>
          <h2 className='medium'>Find the right clients for every opportunity</h2>
          <div className='paragraph-medium'>Ask questions in plain language. Your AI Assistant delivers results in seconds.</div>
        </div>
      </div>
      <div className='d-flex flex-column w-100 zocks-mt-4 align-items-center'>
        <div className='d-flex flex-column w-100 p-4 gap-3 cross-client-input zocks-border-1.25 border border-1 zocks-border-color-gray-200'>
          <div className='d-flex gap-3'>
            <Form.Control
              className='p-0 border-0 cross-client-input-textarea'
              as='textarea'
              value={userQuestion}
              maxLength={MAX_USER_QUESTION_LENGTH}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                setUserQuestion(e.currentTarget.value)
              }}
              disabled={initLoading || !!currentExecution}
              onKeyDown={async (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
                if (initLoading) {
                  return
                }
                if (e.key === 'Enter') {
                  e.preventDefault()
                  if (e.ctrlKey || e.metaKey) {
                    setUserQuestion((chatMessage) => `${chatMessage}\n`)
                  } else if (userQuestion.trim().length >= 3 && userQuestion.trim().length <= MAX_USER_QUESTION_LENGTH) {
                    await initializeCrossClientExecution(userQuestion)
                  }
                }
              }}
              placeholder='Example: Which clients are approaching retirement with over $500k in assets?'
            />
            {!initLoading && !currentExecution ? (
              <Button
                variant='borderless'
                className='button-medium px-0 align-items-end'
                onClick={async () => await initializeCrossClientExecution(userQuestion)}
                disabled={userQuestion.trim().length < 3 || userQuestion.trim().length > MAX_USER_QUESTION_LENGTH}
              >
                <i className='bi bi-arrow-right-circle-fill fs-2' />
              </Button>
            ) : null}
          </div>
          {initLoading ? (
            <div className='d-flex zocks-gap-0.75 align-items-center'>
              <div className='d-flex align-items-center justify-content-center bg-black zocks-border-0.25 cross-client-loader-logo'>
                <ZocksLogo width={12} height={12} />
              </div>
              <div className='paragraph-medium shimmering-loader-text'>Breaking down your question into search criteria…</div>
            </div>
          ) : null}
          {numberOfClauses ? (
            <div className='d-flex flex-column'>
              <div className='d-flex zocks-gap-0.75 align-items-start'>
                <div className='d-flex align-items-center justify-content-center bg-black zocks-border-0.25 cross-client-loader-logo'>
                  <ZocksLogo width={12} height={12} />
                </div>
                <div className='d-flex flex-column gap-3 align-items-start'>
                  <div className='paragraph-medium'>I'll check all clients against {numberOfClauses === 1 ? 'this' : `these ${numberOfClauses}`} criteria:</div>
                  <CrossClientClauses atomicClauses={atomicClauses} />
                  <div className='paragraph-medium'>Does this look right?</div>
                </div>
              </div>
              <div className='d-flex gap-2 align-items-center justify-content-end'>
                <Button variant='header' className='button-medium' onClick={handleRestart} disabled={evaluateLoading}>
                  Ask another question
                </Button>
                <Button variant='primary' className='button-medium' onClick={handleEvaluate} loading={evaluateLoading}>
                  Looks good
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      </div>
      <div
        className={classNames('d-flex flex-column pt-4 align-items-center zocks-gap-2 w-100 fade', {
          'opacity-0': initLoading || currentExecution,
          'opacity-100': !initLoading && !currentExecution,
        })}
      >
        <CrossClientRunningExecutions />
        <CrossClientHistory />
        <CrossClientLandingDefaults />
      </div>
    </div>
  )
}
